import React, { memo } from "react";
import { externalBookingUrl } from "app/utils/externalUrl";
import styles from "./styles.module.scss";
import UserModel from "app/models/UserModel";
import UserAvatar from "../UserAvatar";
import { cls } from "app/utils";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { shallowEqual } from "react-redux";
import { useSelector } from "app/utils/hooks";
import { RootState } from "typedefs";
import { hasFeature } from "app/utils/hasFeature";
import { hasAcademyPlan } from "app/utils/subscription";

interface Props {
  user: UserModel;
  className?: string;
  productCount?: number;
  /**
   * Whether this coach takes bookings through the app — they hold a coach profile
   * at a facility the athlete can book at. Undecided until the roster loads, which
   * is why the button reads "View Profile" until it does.
   */
  bookable?: boolean;
  /**
   * Start booking with this coach (frame 547:73414: Home's coach-specific button
   * opens their calendar on their default option). Without it the card is only a
   * link to the profile, as it was.
   */
  onBookSession?: (user: UserModel) => void;
}

const CoachCard = ({
  user,
  className,
  productCount,
  bookable = false,
  onBookSession,
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { self } = useSelector((state: RootState) => state.user, shallowEqual);

  // The card opens the coach's scheduling landing page, which is where the
  // athlete can actually see and book what the coach offers. That route is gated
  // on the scheduling feature (RouterData), so an athlete without it would land
  // somewhere they cannot open; they keep the app-native profile page instead.
  //
  // The coach has to be a scheduling coach too (CN-2805): a Pro or Plus coach has
  // no sessions, packages or clinics, so their scheduling page renders empty with
  // nothing actionable. Those coaches keep the profile page every plan opened
  // before the booking redesign. `bookable` covers an Academy coach whose plan is
  // absent from this payload — being in the athlete's booking roster proves the
  // page has something to show.
  const coachHasSchedulingPage = hasAcademyPlan(user) || bookable;

  const onCoachCardClick = () => {
    if (hasFeature(self?.features, "scheduling") && coachHasSchedulingPage) {
      history.push(`/sessions/coach/${user?.id}`);
      return;
    }
    history.push(`/home/${user?.id}`);
  };

  // The coach's own booking service, where they have set one. A coachnow.io link
  // is our own booking flow written out longhand, so it is booked in the app.
  // The helper also drops a value that is not a web address at all: the field is
  // free text, and a `javascript:` URL in it used to reach `window.open`.
  const externalLink =
    externalBookingUrl(user?.coachProfile?.bookingServiceLink) || "";
  // In-app booking needs the account's own scheduling access: the roster comes
  // from the scheduling service, which knows the athlete as a student of the
  // academy whether or not it is published, so `bookable` alone would offer a
  // button whose route is gated (RouterData) and which therefore does nothing.
  // The coach's own external booking service is theirs, not ours, and stays.
  const canBook =
    Boolean(externalLink) ||
    (hasFeature(self?.features, "scheduling") && bookable && Boolean(onBookSession));

  const onBookClick = (event: React.SyntheticEvent) => {
    if (!canBook) {
      return;
    }
    // The card underneath opens the profile; this button books.
    event.stopPropagation();
    if (externalLink) {
      const win = window.open(externalLink, "_blank", "noopener,noreferrer");
      if (win) {
        win.opener = null;
      }
      return;
    }
    onBookSession?.(user);
  };

  // Enter and Space do what a click does. A click that cannot book falls through to
  // the card and opens the profile; a key press has nothing to fall through to, so
  // it calls the same handler directly.
  const onBookKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();

    if (canBook) {
      onBookClick(event);
      return;
    }
    onCoachCardClick();
  };

  return (
    <div className={cls(styles.cardWrapper, className)}>
      {productCount > 0 && (
        <div className={styles.offeringsBadge}>
          {t("{{count}} Offering", {count: productCount})}
        </div>
      )}
      <div onClick={onCoachCardClick} className={styles.container}>
        <UserAvatar
          sizeHeight={61}
          sizeWidth={61}
          className={styles.avatar}
          srcName={user?.avatar as string}
          altName="coach avatar"
          userName={user?.displayName}
        />
        <p className={styles.user_name}>{user?.displayName}</p>
        <p className={styles.sport}>{user?.sport}</p>
        {/* Announced and operated as a button, but still a div. `book_session_btn`
            sets no font-family, which a native <button> does not inherit, and the
            element swap turned the pill into a UA-styled slab that clipped the
            coach's name. role + tabIndex + key handling give screen readers and the
            keyboard what they need without touching the box. */}
        <div
          className={styles.book_session_btn}
          role="button"
          tabIndex={0}
          onClick={onBookClick}
          onKeyDown={onBookKeyDown}
        >
          {canBook ? t("Book a Session") : t("View Profile")}
        </div>
      </div>
    </div>
  );
};

export default memo(CoachCard);
